import React, { useState, useEffect, useCallback } from 'react';
import {
  Title2,
  Body1,
  Button,
  Input,
  Select,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  makeStyles,
  tokens,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import {
  ArrowClockwiseRegular,
  ArrowDownloadRegular,
  SearchRegular,
  EyeRegular,
  EyeOffRegular,
  ChevronUpRegular,
  ChevronDownRegular,
  EditRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';
import { getSharePointService } from '../services/sharePointService';
import { Volunteer, Comment as VolunteerComment, getBULeader, getAvailableRoles, getBUManagerDisplay } from '../types/sharepoint';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { SchedulePdf } from '../components/SchedulePdf';

// Canonical departments matching Org Chart (source of truth)
const CANONICAL_DEPARTMENTS = [
  'Marketing and Communications',
  'Clinical Operations',
  'Finance & Development',
  'LEHR',
  'Support Services',
  'Technology'
];

// Helper function to get current clinic month
const getCurrentClinicMonth = () => {
  const today = new Date();
  const currentMonth = today.getMonth();
  
  // Get the 2nd Saturday of the current month
  const firstDay = new Date(today.getFullYear(), currentMonth, 1);
  const firstSaturday = new Date(firstDay);
  firstSaturday.setDate(1 + (6 - firstDay.getDay() + 7) % 7);
  const secondSaturday = new Date(firstSaturday);
  secondSaturday.setDate(firstSaturday.getDate() + 7);
  
  // If we're past the 2nd Saturday, consider it next month
  if (today > secondSaturday) {
    return (currentMonth + 1) % 12;
  }
  
  return currentMonth;
};

// Helper function to get month name
const getMonthName = (monthIndex: number) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  return months[monthIndex];
};

// Helper function to get area badge color
const getAreaBadgeColor = (area: string) => {
  switch (area) {
    case 'Pre-Ops':
      return tokens.colorPaletteBlueBackground2;
    case 'Ops Day Of':
      return tokens.colorPaletteGreenBackground2;
    case 'Greeters':
      return tokens.colorPaletteYellowBackground2;
    case 'Registration':
      return tokens.colorPaletteDarkOrangeBackground2;
    case 'Intake':
      return tokens.colorPalettePinkBackground2;
    case 'Lab':
      return tokens.colorPalettePurpleBackground2;
    case 'Exam Rooms':
      return tokens.colorPaletteTealBackground2;
    case 'Security':
      return tokens.colorPaletteRedBackground2;
    case 'Prayer':
      return tokens.colorPalettePurpleBackground2;
    case 'Translator':
      return tokens.colorPaletteBlueBackground2;
    default:
      return tokens.colorPaletteTealBackground2;
  }
};

// Available area options for dropdowns
const AREA_OPTIONS = [
  'Pre-Ops',
  'Ops Day Of',
  'Greeters',
  'Registration',
  'Intake',
  'Lab',
  'Exam Rooms',
  'Security',
  'Prayer',
  'Translator'
];

// Schedule sections and roles - Updated to match exact structure
const SCHEDULE_SECTIONS = [
  {
    id: 'pre-ops',
    title: 'Pre-Ops',
    roles: [
      { id: 'pre-ops-point', name: 'Point', description: 'Setup Thurs, July 10 @ 6:00p' },
      { id: 'setup-1', name: 'Setup 1' },
      { id: 'setup-2', name: 'Setup 2' },
      { id: 'setup-3', name: 'Setup 3' }
    ]
  },
  {
    id: 'ops-day',
    title: 'Ops Day Of',
    roles: [
      { id: 'ops-point', name: 'Point' },
      { id: 'tear-down-1', name: 'Tear Down 1' },
      { id: 'tear-down-2', name: 'Tear Down 2' },
      { id: 'tear-down-3', name: 'Tear Down 3' },
      { id: 'tear-down-4', name: 'Tear Down 4' },
      { id: 'tear-down-5', name: 'Tear Down 5' }
    ]
  },
  {
    id: 'greeters',
    title: 'Greeters',
    roles: [
      { id: 'greeters-point', name: 'Point' },
      { id: 'greeter-1', name: 'Greeter 1' },
      { id: 'greeter-2', name: 'Greeter 2' },
      { id: 'liaison-1', name: 'Liaison 1' },
      { id: 'liaison-2', name: 'Liaison 2' }
    ]
  },
  {
    id: 'registration',
    title: 'Registration',
    roles: [
      { id: 'registration-point', name: 'Point' },
      { id: 'admin-1', name: 'Admin 1' },
      { id: 'admin-2', name: 'Admin 2' },
      { id: 'admin-3', name: 'Admin 3' }
    ]
  },
  {
    id: 'intake',
    title: 'Intake',
    roles: [
      { id: 'intake-point', name: 'Point' },
      { id: 'nurse-1', name: 'Nurse 1' },
      { id: 'nurse-2', name: 'Nurse 2' }
    ]
  },
  {
    id: 'lab',
    title: 'Lab',
    roles: [
      { id: 'lab-point', name: 'Point' },
      { id: 'lab-1', name: 'Lab 1' },
      { id: 'lab-2', name: 'Lab 2' }
    ]
  },
  {
    id: 'exam-rooms',
    title: 'Exam Rooms',
    roles: [
      { id: 'exam-point', name: 'Point' },
      { id: 'exam-1-provider', name: 'Exam 1 - Provider 1' },
      { id: 'exam-1-support', name: 'Exam 1 - Support 1' },
      { id: 'exam-2-provider', name: 'Exam 2 - Provider 2' },
      { id: 'exam-2-support', name: 'Exam 2 - Support 2' }
    ]
  },
  {
    id: 'security',
    title: 'Security',
    roles: [
      { id: 'security-point', name: 'Point' }
    ]
  },
  {
    id: 'prayer',
    title: 'Prayer',
    roles: [
      { id: 'prayer-point', name: 'Point' },
      { id: 'prayer-1', name: 'Prayer 1' }
    ]
  },
  {
    id: 'translator',
    title: 'Translator',
    roles: [
      { id: 'translator-point', name: 'Point' },
      { id: 'translator-1', name: 'Translator' }
    ]
  }
];

const useStyles = makeStyles({
  container: {
    display: 'flex',
    height: '100%',
    gap: '16px',
    padding: '16px',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      padding: '8px',
      gap: '12px',
    },
  },
  sidebar: {
    width: '280px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
    '@media (max-width: 768px)': {
      width: '100%',
      order: 2,
      maxHeight: '200px',
      overflow: 'auto',
    },
  },
  sidebarHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  searchInput: {
    width: '100%',
  },
  filters: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  filterSelect: {
    width: '100%',
  },
  volunteerList: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  volunteerCard: {
    padding: '6px', // Even smaller from 8px
    backgroundColor: tokens.colorNeutralBackground5,
    borderRadius: '4px', // Smaller border radius
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    transition: 'all 0.2s',
    userSelect: 'none',
    position: 'relative',
    minHeight: '80px', // Much smaller from 100px since button is inline now
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground6,
      boxShadow: tokens.shadow4,
    },
  },
  volunteerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px', // Even smaller from 3px
    marginBottom: '2px', // Even smaller from 3px
  },
  volunteerAvatar: {
    width: '16px', // Much smaller from 20px
    height: '16px', // Much smaller from 20px
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '8px', // Much smaller from 10px
    fontWeight: tokens.fontWeightSemibold,
    color: 'white',
    flexShrink: 0,
  },
  modalAvatar: {
    width: '80px', // Big avatar for modal
    height: '80px', // Big avatar for modal
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px', // Big font for modal
    fontWeight: tokens.fontWeightSemibold,
    color: 'white',
    flexShrink: 0,
  },
  volunteerName: {
    fontSize: '10px', // Even smaller from 11px
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  volunteerTags: {
    display: 'flex',
    gap: '1px', // Keep small
    flexWrap: 'wrap',
    marginBottom: '1px', // Keep small
  },
  roleTag: {
    fontSize: '8px', // Increased from 6px for better readability
    padding: '2px 6px', // Increased from 0px 2px for better visibility
    borderRadius: '6px', // Increased from 4px for better appearance
    fontWeight: tokens.fontWeightSemibold,
    color: 'white', // Ensure text is visible on colored badges
  },
  availabilityStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '1px', // Keep small
    fontSize: '8px', // Even smaller from 9px
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1, // Ensure text is visible
  },
  availabilityDot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    '@media (max-width: 768px)': {
      order: 1,
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  monthSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground4,
      border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
  scheduleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', // Reduced from 400px
    gap: '16px', // Reduced from 20px
    overflow: 'auto',
    flex: 1,
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gap: '12px',
    },
  },
  scheduleSection: {
    minHeight: '180px', // Reduced from 240px
    maxHeight: '280px', // Reduced from 400px
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '4px', // Reduced from 6px
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px', // Reduced from 12px 16px
    backgroundColor: tokens.colorNeutralBackground4,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '4px', // Reduced from 6px
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground5,
    },
  },
  sectionTitle: {
    fontSize: '14px', // Reduced from 16px
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  sectionContent: {
    padding: '4px', // Reduced from 6px
    borderRadius: '2px', // Reduced from 3px
    flex: 1,
    overflow: 'auto',
    maxHeight: '120px', // Added max height for sections with many roles
  },
  previewMode: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `2px solid ${tokens.colorPaletteGreenBackground2}`,
  },
  previewModeHeader: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: tokens.fontWeightMedium,
    textAlign: 'center' as const,
    marginBottom: '8px',
  },
  roleList: {
    padding: '4px', // Reduced from 8px
    gap: '4px', // Reduced from 6px
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', // Reduced from 100px
  },
  roleSlot: {
    padding: '4px 6px', // Reduced from 6px 10px
    borderRadius: '2px', // Reduced from 3px
    backgroundColor: tokens.colorNeutralBackground4,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: '20px', // Reduced from 28px
    gap: '4px', // Reduced from 6px
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '80px', // Reduced from 120px
    maxWidth: '160px', // Reduced from 240px
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  roleSlotFilled: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground5,
  },
  roleSlotDropZone: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: tokens.colorPaletteGreenBackground2,
    opacity: 0.1,
    borderRadius: '4px',
    pointerEvents: 'none',
    transition: 'opacity 0.2s',
  },
  roleName: {
    fontSize: '9px', // Reduced from 11px
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    flex: 1,
  },
  roleDescription: {
    fontSize: '7px', // Reduced from 9px
    color: tokens.colorNeutralForeground3,
    marginTop: '1px', // Reduced from 2px
  },
  assignedVolunteer: {
    gap: '2px', // Reduced from 3px
    fontSize: '8px', // Reduced from 10px
    padding: '1px 3px', // Reduced from 1px 4px
    borderRadius: '2px', // Reduced from 2px
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    display: 'flex',
    alignItems: 'center',
    maxWidth: '60px', // Reduced from 80px
    overflow: 'hidden',
  },
  assignedAvatar: {
    width: '10px', // Reduced from 14px
    height: '10px', // Reduced from 14px
    fontSize: '6px', // Reduced from 7px
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorBrandForeground1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  modalContent: {
    padding: '16px',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  volunteerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  contactInfo: {
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  volunteerDetails: {
    flex: 1,
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  notesSection: {
    marginTop: '20px',
  },
  notesTextarea: {
    width: '100%',
    minHeight: '80px',
    padding: '8px',
    borderRadius: '4px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  wideDialogSurface: {
    width: 'clamp(1000px, 95vw, 1600px)', // Responsive width with min/max, wider than before
    maxWidth: '98vw', // Increased from 95vw for more width
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  monthlySection: {
    marginTop: '20px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  monthlyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
  },
  monthCard: {
    padding: '12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow8,
    },
  },
  hasAvailability: {
    border: '1px solid #107c10',
    backgroundColor: '#dff6dd',
  },
  noAvailability: {
    border: '1px solid #d13438',
    backgroundColor: '#fed9cc',
  },
  noData: {
    border: '1px solid #f0f0f0',
    backgroundColor: '#f9f9f9',
  },
  noDataBadge: {
    backgroundColor: '#f0f0f0',
    color: '#888',
  },
  monthHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  monthIcon: {
    fontSize: '24px',
  },
  monthTitle: {
    fontSize: '14px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  monthContent: {
    width: '100%',
  },
  availabilityDetails: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  availabilityText: {
    fontSize: '12px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  availableBadge: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  noAvailabilityContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  noAvailabilityText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  unavailableBadge: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  monthlySummary: {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  summaryStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  summaryStat: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  statLabel: {
    fontWeight: tokens.fontWeightMedium,
  },
  statValue: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  commentsSection: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  commentsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  commentsLoading: {
    textAlign: 'center',
    padding: '12px',
    color: tokens.colorNeutralForeground2,
  },
  commentsList: {
    marginBottom: '12px',
    maxHeight: '300px',
    overflowY: 'auto',
    paddingRight: '8px',
  },
  commentItem: {
    padding: '12px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  commentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  commentAuthor: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  commentDate: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  commentContent: {
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
    marginTop: '4px',
    wordBreak: 'break-word',
  },
  noComments: {
    textAlign: 'center',
    padding: '12px',
    color: tokens.colorNeutralForeground2,
    fontStyle: 'italic',
  },
  addCommentSection: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  commentInput: {
    width: '100%',
    marginBottom: '12px',
  },
  commentActions: {
    display: 'flex',
    gap: '8px',
  },
  areasSection: {
    marginTop: '20px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  areasGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
  },
  areaCard: {
    padding: '12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  areaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: '8px',
  },
  areaLabel: {
    fontSize: '14px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  areaBadge: {
    fontSize: '10px',
    fontWeight: tokens.fontWeightMedium,
  },
  areaContent: {
    width: '100%',
  },
  areaInfo: {
    fontSize: '14px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  areaDescription: {
    fontSize: '14px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },
  noAreaContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  noAreaText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  noAreaBadge: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
  scheduleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px', // Reduced from 12px
    padding: '6px 8px', // Reduced from 8px 12px
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '3px', // Reduced from 4px
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  scheduleTitle: {
    fontSize: '11px', // Reduced from 13px
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  scheduleActions: {
    display: 'flex',
    gap: '4px', // Reduced from 6px
    alignItems: 'center',
  },
  progressText: {
    fontSize: '9px', // Reduced from 10px
    color: tokens.colorNeutralForeground2,
    marginRight: '6px', // Reduced from 8px
  },
  scheduleButton: {
    fontSize: '8px', // Reduced from 9px
    padding: '2px 6px', // Reduced from 3px 8px
    minHeight: '20px', // Reduced from 24px
    borderRadius: '2px', // Reduced from 3px
  },
});

interface Assignment {
  roleId: string;
  volunteerId: string;
  volunteerName: string;
  volunteerAvatar: string;
}

// Clickable Volunteer Card Component
const ClickableVolunteerCard: React.FC<{
  volunteer: Volunteer;
  currentMonthName: string;
  getVolunteerAvailability: (volunteer: Volunteer) => string;
  getAreaBadgeColor: (area: string) => string;
  onClick: (volunteer: Volunteer) => void;
  onViewMore: (volunteer: Volunteer) => void;
  isSelected: boolean;
  onQuickDepartmentChange?: (volunteer: Volunteer, department: string) => void;
}> = ({ volunteer, currentMonthName, getVolunteerAvailability, getAreaBadgeColor, onClick, onViewMore, isSelected, onQuickDepartmentChange }) => {
  const styles = useStyles();

  const availability = getVolunteerAvailability(volunteer);
  const isAvailable = availability === 'Available';

  const handleViewMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card's onClick
    onViewMore(volunteer);
  };

  return (
    <div
      className={styles.volunteerCard}
      onClick={() => onClick(volunteer)}
      style={{
        cursor: 'pointer',
        border: isSelected ? `2px solid ${tokens.colorBrandBackground}` : `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: isSelected ? tokens.colorNeutralBackground6 : tokens.colorNeutralBackground5,
        position: 'relative', // For positioning the View More button
      }}
    >
      {/* Selection Indicator */}
      {isSelected && (
        <div 
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '20px',
            height: '20px',
            backgroundColor: '#0078d4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: 'white',
            zIndex: 1,
          }}
          title="Selected for assignment"
        >
          ✓
        </div>
      )}
      
      <div className={styles.volunteerHeader}>
        <div className={styles.volunteerName}>
          {volunteer.name || 'Unknown Volunteer'}
          {(() => {
            try {
              if (!volunteer.createdAt) return null;
              const created = new Date(volunteer.createdAt as string);
              const days = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
              if (days <= 90) {
                return <span style={{
                  marginLeft: 4,
                  backgroundColor: tokens.colorPaletteGreenBackground2,
                  color: tokens.colorPaletteGreenForeground2,
                  borderRadius: '8px',
                  padding: '1px 4px',
                  fontSize: '8px',
                  fontWeight: tokens.fontWeightSemibold,
                }}>NEW</span>;
              }
              return null;
            } catch { return null; }
          })()}
        </div>
      </div>
      
      <div className={styles.volunteerTags}>
        {volunteer.primaryArea && (
          <div 
            className={styles.roleTag}
            style={{ backgroundColor: getAreaBadgeColor(volunteer.primaryArea) }}
          >
            {volunteer.primaryArea}
          </div>
        )}
        {volunteer.secondaryArea && (
          <div 
            className={styles.roleTag}
            style={{ backgroundColor: getAreaBadgeColor(volunteer.secondaryArea) }}
          >
            {volunteer.secondaryArea}
          </div>
        )}
      </div>
      
      <div className={styles.availabilityStatus}>
        <div 
          className={styles.availabilityDot}
          style={{ backgroundColor: isAvailable ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2 }}
        />
        {isAvailable ? 'Available' : 'Not Available'} ({currentMonthName})
        <Button
          size="small"
          appearance="subtle"
          onClick={handleViewMoreClick}
          style={{
            marginLeft: 'auto',
            fontSize: '8px',
            padding: '2px 6px',
            minHeight: '16px',
          }}
        >
          View More
        </Button>
      </div>

      {/* Department change removed from quick-read card; handled in the modal */}
    </div>
  );
};

// Droppable Role Slot Component
const ClickableRoleSlot: React.FC<{
  role: { id: string; name: string; description?: string };
  assignments: Assignment[];
  onRemoveAssignment: (volunteerId: string) => void;
  onRoleClick: (roleId: string) => void;
  isCollapsed: boolean;
  sectionRoleCount: number;
  isSelected: boolean;
}> = ({ role, assignments, onRemoveAssignment, onRoleClick, isCollapsed, sectionRoleCount, isSelected }) => {
  const styles = useStyles();

  const roleAssignments = assignments.filter(a => a.roleId === role.id);
  const isFilled = roleAssignments.length > 0;

  return (
    <div
      className={`${styles.roleSlot} ${isFilled ? styles.roleSlotFilled : ''}`}
      onClick={() => onRoleClick(role.id)}
      style={{
        border: isFilled ? `1px solid ${tokens.colorNeutralStroke1}` : `1px dashed ${tokens.colorNeutralStroke2}`,
        backgroundColor: isFilled ? tokens.colorNeutralBackground1 : tokens.colorNeutralBackground2,
        cursor: isSelected ? 'pointer' : 'default',
        ...(isSelected && !isFilled && {
          border: `2px solid ${tokens.colorPaletteBlueBackground2}`,
          backgroundColor: tokens.colorPaletteBlueBackground2,
          boxShadow: tokens.shadow8,
        }),
        ...(isCollapsed && {
          padding: '6px 8px',
          fontSize: '11px',
          fontWeight: tokens.fontWeightMedium,
        }),
        minWidth: sectionRoleCount <= 3 ? '180px' : sectionRoleCount <= 6 ? '160px' : '140px',
        maxWidth: sectionRoleCount <= 3 ? '280px' : sectionRoleCount <= 6 ? '240px' : '200px',
      }}
    >
      <div className={styles.roleName}>{role.name}</div>
      {roleAssignments.map((assignment) => (
        <div 
          key={assignment.volunteerId} 
          className={styles.assignedVolunteer}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 6px',
            backgroundColor: tokens.colorNeutralBackground3,
            borderRadius: '3px',
            border: `1px solid ${tokens.colorNeutralStroke1}`,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onClick={() => onRemoveAssignment(assignment.volunteerId)}
          title={`Click to remove assignment`}
        >
          <div className={styles.assignedAvatar} style={{
            width: isCollapsed ? '14px' : '16px',
            height: isCollapsed ? '14px' : '16px',
            fontSize: isCollapsed ? '7px' : '8px',
          }}>
            {assignment.volunteerAvatar}
          </div>
          <span style={{ 
            fontSize: isCollapsed ? '9px' : '11px', 
            fontWeight: tokens.fontWeightMedium 
          }}>
            {assignment.volunteerName}
          </span>
          <span 
            style={{ 
              color: tokens.colorNeutralForeground3, 
              fontSize: isCollapsed ? '9px' : '11px',
              fontWeight: tokens.fontWeightMedium,
              opacity: 0.7,
            }}
          >
            ✗
          </span>
        </div>
      ))}
      {!isFilled && (
        <div style={{ 
          color: tokens.colorNeutralForeground3, 
          fontSize: isCollapsed ? '9px' : '11px',
          fontStyle: 'italic',
          padding: '2px 6px',
        }}>
          {isSelected ? 'Click to assign selected volunteer' : 'Click to assign volunteer'}
        </div>
      )}
    </div>
  );
};

const PreviewOverlay: React.FC<{
  assignments: Assignment[];
  currentMonthName: string;
  onClose: () => void;
}> = ({ assignments, currentMonthName, onClose }) => {
  
  const getSectionCompletion = (sectionId: string) => {
    const section = SCHEDULE_SECTIONS.find(s => s.id === sectionId);
    if (!section) return { filled: 0, total: 0, percentage: 0 };
    
    const sectionRoleIds = section.roles.map(role => role.id);
    const filled = assignments.filter(a => sectionRoleIds.includes(a.roleId)).length;
    const total = section.roles.length;
    const percentage = total > 0 ? Math.round((filled / total) * 100) : 0;
    
    return { filled, total, percentage };
  };

  const getOverallCompletion = () => {
    const totalRoles = SCHEDULE_SECTIONS.reduce((sum, section) => sum + section.roles.length, 0);
    const totalFilled = assignments.length;
    const percentage = totalRoles > 0 ? Math.round((totalFilled / totalRoles) * 100) : 0;
    
    return { filled: totalFilled, total: totalRoles, percentage };
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'white',
      zIndex: 1000,
      overflow: 'auto',
      padding: '20px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px',
        borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
        marginBottom: '20px',
      }}>
        <div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: tokens.fontWeightBold,
            color: tokens.colorNeutralForeground1,
            margin: 0,
          }}>
            TruCare Clinic Schedule
          </h1>
          <p style={{
            fontSize: '16px',
            color: tokens.colorNeutralForeground3,
            margin: '8px 0 0 0',
          }}>
            {currentMonthName} - Preview Mode
          </p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: tokens.colorNeutralBackground2,
            borderRadius: '6px',
            border: `1px solid ${tokens.colorNeutralStroke1}`,
          }}>
            <span style={{ fontSize: '14px', fontWeight: tokens.fontWeightMedium }}>
              Overall Progress: {getOverallCompletion().filled}/{getOverallCompletion().total}
            </span>
            <Badge 
              appearance={getOverallCompletion().percentage === 100 ? 'filled' : 'tint'}
              color={getOverallCompletion().percentage === 100 ? 'success' : 'brand'}
            >
              {getOverallCompletion().percentage}% Complete
            </Badge>
          </div>
          
          <Button
            appearance="outline"
            onClick={onClose}
            icon={<EyeOffRegular />}
          >
            Exit Preview
          </Button>
        </div>
      </div>

      {/* Schedule Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        maxWidth: '1400px',
        margin: '0 auto',
      }}>
        {SCHEDULE_SECTIONS.map((section) => {
          const completion = getSectionCompletion(section.id);
          
          return (
            <div key={section.id} style={{
              border: `2px solid ${tokens.colorNeutralStroke1}`,
              borderRadius: '8px',
              overflow: 'hidden',
            }}>
              {/* Section Header */}
              <div style={{
                padding: '16px 20px',
                backgroundColor: tokens.colorNeutralBackground1,
                borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: tokens.fontWeightSemibold,
                  color: tokens.colorNeutralForeground1,
                  margin: 0,
                }}>
                  {section.title}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: tokens.colorNeutralForeground3 }}>
                    {completion.filled}/{completion.total}
                  </span>
                  <Badge 
                    appearance={completion.percentage === 100 ? 'filled' : 'tint'}
                    color={completion.percentage === 100 ? 'success' : 'brand'}
                  >
                    {completion.percentage === 100 ? 'Complete' : `${completion.percentage}%`}
                  </Badge>
                </div>
              </div>

              {/* Section Roles */}
              <div style={{ padding: '16px' }}>
                {section.roles.map((role) => {
                  const roleAssignments = assignments.filter(a => a.roleId === role.id);
                  const isFilled = roleAssignments.length > 0;
                  
                  return (
                    <div key={role.id} style={{
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: isFilled ? tokens.colorNeutralBackground1 : tokens.colorNeutralBackground3,
                      border: `1px solid ${isFilled ? tokens.colorNeutralStroke1 : tokens.colorNeutralStroke2}`,
                      borderRadius: '6px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: tokens.fontWeightSemibold,
                          color: tokens.colorNeutralForeground1,
                        }}>
                          {role.name}
                        </div>
                        {role.description && (
                          <div style={{
                            fontSize: '12px',
                            color: tokens.colorNeutralForeground3,
                            fontStyle: 'italic',
                            marginTop: '2px',
                          }}>
                            {role.description}
                          </div>
                        )}
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isFilled ? (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            backgroundColor: tokens.colorPaletteGreenBackground1,
                            borderRadius: '4px',
                            border: `1px solid ${tokens.colorPaletteGreenBackground2}`,
                          }}>
                            <div style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: tokens.colorBrandBackground,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: tokens.fontWeightSemibold,
                              color: 'white',
                            }}>
                              {roleAssignments[0].volunteerAvatar}
                            </div>
                            <span style={{
                              fontSize: '13px',
                              fontWeight: tokens.fontWeightMedium,
                              color: tokens.colorNeutralForeground1,
                            }}>
                              {roleAssignments[0].volunteerName}
                            </span>
                          </div>
                        ) : (
                          <div style={{
                            padding: '6px 12px',
                            backgroundColor: tokens.colorNeutralBackground2,
                            borderRadius: '4px',
                            border: `1px dashed ${tokens.colorNeutralStroke2}`,
                            color: tokens.colorNeutralForeground3,
                            fontSize: '12px',
                            fontStyle: 'italic',
                          }}>
                            Unassigned
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const VolunteerScheduler: React.FC = () => {
  const styles = useStyles();
  
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState<Volunteer[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentClinicMonth());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [primaryAreaFilter, setPrimaryAreaFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [contactStatusFilter, setContactStatusFilter] = useState('Contact Complete'); // Default to Active
  const [onboardingGroupFilter, setOnboardingGroupFilter] = useState('all');
  const [selectedVolunteerForAssignment, setSelectedVolunteerForAssignment] = useState<Volunteer | null>(null);
  const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOverlayOpen, setIsPreviewOverlayOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState<VolunteerComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [updatingMonth, setUpdatingMonth] = useState<string | null>(null);
  const [isEditingContact, setIsEditingContact] = useState(false);

  const currentMonthName = getMonthName(selectedMonth);

  const loadVolunteers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getSharePointService().getVolunteers();
      // Filter for scheduling team volunteers
      const schedulingVolunteers = data.filter(v => v.assignedManager === 'Scheduling');
      setVolunteers(schedulingVolunteers);
    } catch (err) {
      console.error('Error loading volunteers:', err);
      setError('Failed to load volunteers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateVolunteer = useCallback(async (id: string, updates: Partial<Volunteer>) => {
    try {
      setVolunteers(prevVolunteers => 
        prevVolunteers.map(volunteer => 
          volunteer.id === id 
            ? { ...volunteer, ...updates }
            : volunteer
        )
      );
      
      await getSharePointService().updateVolunteer(id, updates);
      console.log('✅ Backend update completed successfully');
    } catch (error) {
      console.error('❌ Error updating volunteer:', error);
      await loadVolunteers();
      throw error;
    }
  }, [loadVolunteers]);

  const handleContactUpdate = useCallback(async (id: string, field: string, value: string) => {
    try {
      const updates = { [field]: value };
      await updateVolunteer(id, updates);
      
      // Update the selectedVolunteer state to reflect changes immediately
      setSelectedVolunteer(prev => prev ? { ...prev, ...updates } : null);
    } catch (error) {
      console.error('Error updating contact information:', error);
    }
  }, [updateVolunteer]);

  const filterVolunteers = useCallback(() => {
    let filtered = [...volunteers];

    if (searchTerm) {
      filtered = filtered.filter(volunteer =>
        volunteer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (primaryAreaFilter !== 'all') {
      filtered = filtered.filter(volunteer => volunteer.primaryArea === primaryAreaFilter);
    }

    if (availabilityFilter !== 'all') {
      const monthKey = `${currentMonthName.toLowerCase()}Availability` as keyof Volunteer;
      filtered = filtered.filter(volunteer => {
        const availability = volunteer[monthKey] as string;
        if (availabilityFilter === 'available') {
          return availability === 'Available';
        } else if (availabilityFilter === 'unavailable') {
          return availability === 'Not Available';
        }
        return true;
      });
    }

    if (contactStatusFilter !== 'all') {
      filtered = filtered.filter(volunteer => volunteer.volunteerStatus === contactStatusFilter);
    }

    if (onboardingGroupFilter !== 'all') {
      filtered = filtered.filter(volunteer => volunteer.assignedManager === onboardingGroupFilter);
    }

    setFilteredVolunteers(filtered);
  }, [volunteers, searchTerm, primaryAreaFilter, availabilityFilter, contactStatusFilter, onboardingGroupFilter, currentMonthName]);

  useEffect(() => {
    filterVolunteers();
  }, [filterVolunteers]);

  // Removed debug comments useEffect as comments state was removed

  useEffect(() => {
    loadVolunteers();
  }, [loadVolunteers]);

  // Listen for global updates from other pages (e.g., Org Chart) and update locally without full reload
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ volunteerId: string; updates: Partial<Volunteer> }>;
      const detail = ce.detail || ({} as any);
      const { volunteerId, updates } = detail;
      if (!volunteerId) return;

      setVolunteers(prev => prev.map(v => v.id === volunteerId ? { ...v, ...updates } : v));
      setFilteredVolunteers(prev => prev.map(v => v.id === volunteerId ? { ...v, ...updates } : v));
    };
    window.addEventListener('volunteer-updated', handler as EventListener);
    return () => window.removeEventListener('volunteer-updated', handler as EventListener);
  }, []);

  const getVolunteerAvailability = (volunteer: Volunteer) => {
    const monthKey = `${currentMonthName.toLowerCase()}Availability` as keyof Volunteer;
    const availability = volunteer[monthKey] as string;
    
    // Debug logging to see what we're getting
    console.log(`🔍 Availability for ${volunteer.name} in ${currentMonthName}:`, {
      monthKey,
      availability,
      volunteer: {
        juneAvailability: volunteer.juneAvailability,
        julyAvailability: volunteer.julyAvailability,
        augustAvailability: volunteer.augustAvailability,
      }
    });
    
    return availability || 'Not Available';
  };



  const handleRemoveAssignment = (volunteerId: string) => {
    setAssignments(prev => prev.filter(a => a.volunteerId !== volunteerId));
    console.log(`🗑️ Removed volunteer ${volunteerId} from assignment`);
  };

  const toggleSectionCollapse = (sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const getSectionCompletion = (sectionId: string) => {
    const section = SCHEDULE_SECTIONS.find(s => s.id === sectionId);
    if (!section) return { filled: 0, total: 0, percentage: 0 };
    
    const sectionRoleIds = section.roles.map(role => role.id);
    const filled = assignments.filter(a => sectionRoleIds.includes(a.roleId)).length;
    const total = section.roles.length;
    const percentage = total > 0 ? Math.round((filled / total) * 100) : 0;
    
    return { filled, total, percentage };
  };

  const getOverallCompletion = () => {
    const totalRoles = SCHEDULE_SECTIONS.reduce((sum, section) => sum + section.roles.length, 0);
    const totalFilled = assignments.length;
    const percentage = totalRoles > 0 ? Math.round((totalFilled / totalRoles) * 100) : 0;
    
    return { filled: totalFilled, total: totalRoles, percentage };
  };



  const handleVolunteerSelectForAssignment = (volunteer: Volunteer) => {
    setSelectedVolunteerForAssignment(volunteer);
  };

  const handleViewMoreVolunteer = (volunteer: Volunteer) => {
    console.log('🔍 Opening modal for volunteer:', volunteer.name, 'ID:', volunteer.id);
    setSelectedVolunteer(volunteer);
    setIsModalOpen(true);
    loadComments(volunteer.id); // Load comments when modal opens
  };

  const loadComments = async (volunteerId: string) => {
    try {
      console.log('🔍 Loading comments for volunteer ID:', volunteerId);
      setLoadingComments(true);
      
      // Use existing Volunteer Comments list - no creation needed
      const service = getSharePointService();
      console.log('ℹ️ Using existing Volunteer Comments list');
      
      const volunteerComments = await service.getVolunteerCommentsFromList(volunteerId);
      console.log('📝 Comments loaded:', volunteerComments);
      setComments(volunteerComments);
    } catch (error) {
      console.error('❌ Error loading comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const addVolunteerComment = async (volunteerId: string, comment: string) => {
    try {
      console.log('🔍 Adding comment for volunteer ID:', volunteerId, 'Comment:', comment);
      const service = getSharePointService();
      
      // Add the comment using the regular method
      await service.addVolunteerCommentToList(volunteerId, selectedVolunteer?.name || 'Unknown', comment);
      
      console.log('✅ Comment added successfully');
      // Reload comments after adding
      await loadComments(volunteerId);
    } catch (error) {
      console.error('❌ Error adding comment:', error);
    }
  };

  const handleAddComment = async () => {
    if (selectedVolunteer && newComment.trim()) {
      setSubmittingComment(true);
      try {
        await addVolunteerComment(selectedVolunteer.id, newComment.trim());
        setNewComment('');
        // Reload comments to show the new one
        await loadComments(selectedVolunteer.id);
      } catch (error) {
        console.error('Error adding comment:', error);
      } finally {
        setSubmittingComment(false);
      }
    }
  };

  const handleRoleClick = (roleId: string) => {
    if (selectedVolunteerForAssignment) {
      // Assign the selected volunteer to this role
      const newAssignment: Assignment = {
        roleId: roleId,
        volunteerId: selectedVolunteerForAssignment.id,
        volunteerName: selectedVolunteerForAssignment.name || '',
        volunteerAvatar: selectedVolunteerForAssignment.name?.charAt(0) || 'V'
      };
      
      // Remove any existing assignment for this role
      const updatedAssignments = assignments.filter(a => a.roleId !== roleId);
      // Add the new assignment
      setAssignments([...updatedAssignments, newAssignment]);
      
      // Clear the selection
      setSelectedVolunteerForAssignment(null);
    }
  };

  // Calculate the second Saturday of the selected month
  const getSecondSaturday = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = selectedMonth; // Use selectedMonth instead of current month
    
    // Get the first day of the selected month
    const firstDay = new Date(year, month, 1);
    // Get the first Saturday (6 = Saturday)
    const firstSaturday = new Date(firstDay);
    const daysUntilFirstSaturday = (6 - firstDay.getDay() + 7) % 7;
    firstSaturday.setDate(1 + daysUntilFirstSaturday);
    
    // Get the second Saturday (7 days after the first)
    const secondSaturday = new Date(firstSaturday);
    secondSaturday.setDate(firstSaturday.getDate() + 7);
    
    return secondSaturday;
  };

  const clinicDate = getSecondSaturday();
  const arrivalTime = "8:00 AM";

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Title2>Volunteer Pool</Title2>
          
          <Input
            className={styles.searchInput}
            placeholder="Search volunteers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            contentBefore={<SearchRegular style={{ fontSize: '14px' }} />}
          />
          
          <div className={styles.filters}>
            <Select
              className={styles.filterSelect}
              value={primaryAreaFilter}
              onChange={(e) => setPrimaryAreaFilter(e.target.value)}
            >
                              <option value="all">All Clinic Day Areas</option>
              <option value="Intake">Intake</option>
              <option value="Lab">Lab</option>
              <option value="Registration">Registration</option>
              <option value="Liaison">Liaison</option>
              <option value="Provider Support">Provider Support</option>
              <option value="Prayer">Prayer</option>
              <option value="Translator">Translator</option>
            </Select>
            
            <Select
              className={styles.filterSelect}
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
            >
              <option value="all">All Availability</option>
              <option value="available">Available</option>
              <option value="unavailable">Not Available</option>
            </Select>
            
            <Select
              className={styles.filterSelect}
              value={contactStatusFilter}
              onChange={(e) => setContactStatusFilter(e.target.value)}
            >
              <option value="all">All Contact Status</option>
              <option value="Contact Complete">Active</option>
              <option value="New Needs Contact">New Needs Contact</option>
              <option value="Contacted, No Response">Contacted, No Response</option>
              <option value="Inactive">Inactive</option>
            </Select>
            
            <Select
              className={styles.filterSelect}
              value={onboardingGroupFilter}
              onChange={(e) => setOnboardingGroupFilter(e.target.value)}
            >
              <option value="all">All Onboarding Group</option>
              <option value="Compliance">Compliance</option>
              <option value="Scheduling">Scheduling</option>
              <option value="Verification">Verification</option>
            </Select>
          </div>
        </div>
        
        <div className={styles.volunteerList}>
          {filteredVolunteers.map((volunteer) => (
            <ClickableVolunteerCard
              key={volunteer.id}
              volunteer={volunteer}
              currentMonthName={currentMonthName}
              getVolunteerAvailability={getVolunteerAvailability}
              getAreaBadgeColor={getAreaBadgeColor}
              onClick={handleVolunteerSelectForAssignment}
              onViewMore={handleViewMoreVolunteer}
              isSelected={selectedVolunteerForAssignment?.id === volunteer.id}
              onQuickDepartmentChange={(v, dept) => {
                updateVolunteer(v.id, { businessUnit: dept });
              }}
            />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <Title2>Clinic Schedule</Title2>
            <Body1>Click a volunteer to select, then click a role to assign</Body1>
            {(() => {
              const completion = getOverallCompletion();
              return (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginTop: '4px',
                  fontSize: '14px',
                  color: tokens.colorNeutralForeground3
                }}>
                  <span>Overall Progress: {completion.filled}/{completion.total} roles filled</span>
                  <Badge 
                    appearance={completion.percentage === 100 ? 'filled' : 'tint'}
                    color={completion.percentage === 100 ? 'success' : 'brand'}
                  >
                    {completion.percentage}% Complete
                  </Badge>
                </div>
              );
            })()}
          </div>
          
          <div className={styles.headerActions}>
            <Button
              appearance="outline"
              onClick={() => setIsPreviewOverlayOpen(true)}
              icon={<EyeRegular />}
            >
              Preview Schedule
            </Button>
          </div>

          <div className={styles.monthSelector}>
            <Select
              value={selectedMonth.toString()}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i}>{getMonthName(i)}</option>
              ))}
            </Select>
            
            <div className={styles.actionButtons}>
              <button 
                className={styles.actionButton}
                onClick={loadVolunteers}
                title="Refresh Data"
              >
                <ArrowClockwiseRegular style={{ fontSize: '14px' }} />
                Refresh
              </button>
              <PDFDownloadLink
                document={
                  <SchedulePdf
                    sections={SCHEDULE_SECTIONS}
                    assignments={assignments}
                    volunteers={volunteers}
                    clinicDate={clinicDate}
                    arrivalTime={arrivalTime}
                  />
                }
                fileName={`clinic-schedule-${currentMonthName.toLowerCase()}-${new Date().getFullYear()}.pdf`}
              >
                {({ loading }) => (
                  <button 
                    className={styles.actionButton}
                    title="Export Schedule"
                    disabled={loading}
                  >
                    <ArrowDownloadRegular style={{ fontSize: '14px' }} />
                    {loading ? 'Generating...' : 'Export'}
                  </button>
                )}
              </PDFDownloadLink>
            </div>
          </div>
        </div>
        
        {error && (
          <div style={{ 
            backgroundColor: tokens.colorPaletteRedBackground2, 
            color: tokens.colorPaletteRedForeground2,
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}
        
        <div className={styles.scheduleGrid}>
          {SCHEDULE_SECTIONS.map((section) => {
            const completion = getSectionCompletion(section.id);
            const isCollapsed = collapsedSections.has(section.id);
            
            return (
              <div
                key={section.id}
                className={styles.scheduleSection}
                style={{
                  maxHeight: isCollapsed ? '60px' : 'none',
                }}
              >
                <div 
                  className={styles.sectionHeader}
                  onClick={() => toggleSectionCollapse(section.id)}
                >
                  <div className={styles.sectionTitle}>
                    {section.title}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      fontSize: '12px',
                      color: tokens.colorNeutralForeground3
                    }}>
                      <span>{completion.filled}/{completion.total}</span>
                      <Badge 
                        appearance={completion.percentage === 100 ? 'filled' : 'tint'}
                        color={completion.percentage === 100 ? 'success' : 'brand'}
                      >
                        {completion.percentage === 100 ? 'Complete' : `${completion.percentage}%`}
                      </Badge>
                    </div>
                    <span style={{ 
                      fontSize: '12px',
                      color: tokens.colorNeutralForeground3,
                      transition: 'transform 0.2s',
                      transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
                    }}>
                      ▼
                    </span>
                  </div>
                </div>
                
                {!isCollapsed && (
                  <div className={styles.sectionContent} style={{
                    padding: isCollapsed ? '8px' : '12px',
                  }}>
                    <div className={styles.roleList} style={{
                      display: 'grid',
                      gridTemplateColumns: section.roles.length <= 3 ? 'repeat(auto-fit, minmax(180px, 1fr))' : 
                                         section.roles.length <= 6 ? 'repeat(auto-fit, minmax(160px, 1fr))' : 
                                         'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: isCollapsed ? '6px' : '8px',
                    }}>
                      {section.roles.map((role) => (
                        <ClickableRoleSlot
                          key={role.id}
                          role={role}
                          assignments={assignments}
                          onRemoveAssignment={handleRemoveAssignment}
                          onRoleClick={handleRoleClick}
                          isCollapsed={isCollapsed}
                          sectionRoleCount={section.roles.length}
                          isSelected={selectedVolunteerForAssignment !== null}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Volunteer Details Modal */}
      {selectedVolunteer && isModalOpen && (
        <Dialog open={isModalOpen} onOpenChange={() => setIsModalOpen(false)}>
          <DialogSurface className={styles.wideDialogSurface}>
            <DialogBody>
              <DialogTitle>Volunteer Details</DialogTitle>
              <DialogContent>
                <div className={styles.modalContent}>
                  <div className={styles.volunteerInfo}>
                    <div className={styles.modalAvatar}>
                      {selectedVolunteer?.name?.charAt(0) || 'V'}
                    </div>
                    <div className={styles.volunteerDetails}>
                      <div style={{ fontWeight: tokens.fontWeightSemibold, fontSize: '16px' }}>
                        {selectedVolunteer?.name || 'Unknown Volunteer'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Contact Information and Service Areas */}
                  <div className={styles.contactInfo}>
                    <div style={{ 
                      display: 'flex', 
                      gap: '24px',
                      height: '100%'
                    }}>
                      {/* Contact Information - Left Half */}
                      <div style={{ 
                        flex: 1,
                        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
                        paddingRight: '24px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          marginBottom: '8px' 
                        }}>
                          <div style={{ fontWeight: tokens.fontWeightSemibold }}>
                            Contact Information
                          </div>
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={isEditingContact ? <CheckmarkRegular /> : <EditRegular />}
                            onClick={() => setIsEditingContact(!isEditingContact)}
                          >
                            {isEditingContact ? 'Save' : 'Edit'}
                          </Button>
                        </div>
                        
                        {/* Email Field */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                            Email
                          </div>
                          {isEditingContact ? (
                            <Input
                              value={selectedVolunteer?.email || ''}
                              onChange={(e) => handleContactUpdate(selectedVolunteer.id, 'email', e.target.value)}
                              placeholder="Enter email address"
                              style={{ width: '100%' }}
                            />
                          ) : (
                            <div style={{ 
                              fontSize: '14px', 
                              color: tokens.colorNeutralForeground1,
                              padding: '8px 12px',
                              border: `1px solid ${tokens.colorNeutralStroke2}`,
                              borderRadius: '4px',
                              backgroundColor: tokens.colorNeutralBackground1
                            }}>
                              {selectedVolunteer?.email || 'No email provided'}
                            </div>
                          )}
                        </div>
                        
                        {/* Phone Field */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                            Phone Number
                          </div>
                          {isEditingContact ? (
                            <Input
                              value={selectedVolunteer?.phone || ''}
                              onChange={(e) => {
                                const phoneValue = e.target.value;
                                handleContactUpdate(selectedVolunteer.id, 'phone', phoneValue);
                                // Automatically update WhatsApp to match phone number
                                handleContactUpdate(selectedVolunteer.id, 'whatsapp', phoneValue);
                              }}
                              placeholder="Enter phone number"
                              style={{ width: '100%' }}
                            />
                          ) : (
                            <div style={{ 
                              fontSize: '14px', 
                              color: tokens.colorNeutralForeground1,
                              padding: '8px 12px',
                              border: `1px solid ${tokens.colorNeutralStroke2}`,
                              borderRadius: '4px',
                              backgroundColor: tokens.colorNeutralBackground1
                            }}>
                              {selectedVolunteer?.phone || 'No phone number provided'}
                            </div>
                          )}
                        </div>
                        
                        {/* Department Director - Added underneath telephone number */}
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
                          <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '8px' }}>
                            Department Director
                          </div>
                          <div style={{ 
                            fontSize: '14px', 
                            color: tokens.colorNeutralForeground1,
                            padding: '8px 12px',
                            border: `1px solid ${tokens.colorNeutralStroke2}`,
                            borderRadius: '4px',
                            backgroundColor: tokens.colorNeutralBackground1
                          }}>
                            {getBULeader(selectedVolunteer?.businessUnit) || 'Not assigned'}
                          </div>
                        </div>
                        
                        {/* Manager - Moved to left half near Department Director */}
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '8px' }}>
                            Manager
                          </div>
                          <div style={{ 
                            fontSize: '14px', 
                            color: tokens.colorNeutralForeground1,
                            padding: '8px 12px',
                            border: `1px solid ${tokens.colorNeutralStroke2}`,
                            borderRadius: '4px',
                            backgroundColor: tokens.colorNeutralBackground1
                          }}>
                            {getBUManagerDisplay(selectedVolunteer) || 'Not assigned'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Service Areas - Right Half */}
                      <div style={{ flex: 1, paddingLeft: '24px' }}>
                        <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '8px' }}>
                          Service Areas
                        </div>
                        
                        {/* Clinic Day Area and Department - Side by side */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '12px', 
                          marginBottom: '12px',
                          alignItems: 'flex-start'
                        }}>
                          {/* Clinic Day Area */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                              Clinic Day Area
                            </div>
                            <Select
                              value={selectedVolunteer?.primaryArea || ''}
                              onChange={(e, data) => {
                                if (selectedVolunteer) {
                                  updateVolunteer(selectedVolunteer.id, { primaryArea: data.value || '' });
                                  setSelectedVolunteer(prev => prev ? { ...prev, primaryArea: data.value || '' } : null);
                                }
                              }}
                              style={{ width: '100%' }}
                            >
                              <option value="">Select Clinic Day Area</option>
                              {AREA_OPTIONS.map(area => (
                                <option key={area} value={area}>{area}</option>
                              ))}
                            </Select>
                          </div>
                          
                          {/* Department - Next to Primary Area */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                              Department
                            </div>
                            <Select
                              value={selectedVolunteer?.businessUnit || ''}
                              onChange={(e, data) => {
                                if (selectedVolunteer) {
                                  updateVolunteer(selectedVolunteer.id, { businessUnit: data.value || '' });
                                  setSelectedVolunteer(prev => prev ? { ...prev, businessUnit: data.value || '' } : null);
                                }
                              }}
                              style={{ width: '100%' }}
                            >
                              <option value="">Select Department</option>
                              {CANONICAL_DEPARTMENTS.map(unit => (
                                <option key={unit} value={unit}>{unit}</option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        
                        {/* Secondary Area and Role - Side by side */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '12px', 
                          marginBottom: '12px',
                          alignItems: 'flex-start'
                        }}>
                          {/* Secondary Area */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                              Secondary Area
                            </div>
                            <Select
                              value={selectedVolunteer?.secondaryArea || ''}
                              onChange={(e, data) => {
                                if (selectedVolunteer) {
                                  updateVolunteer(selectedVolunteer.id, { secondaryArea: data.value || '' });
                                  setSelectedVolunteer(prev => prev ? { ...prev, secondaryArea: data.value || '' } : null);
                                }
                              }}
                              style={{ width: '100%' }}
                            >
                              <option value="">Select Secondary Area</option>
                              {AREA_OPTIONS.map(area => (
                                <option key={area} value={area}>{area}</option>
                              ))}
                            </Select>
                          </div>
                          
                          {/* Role - Beside Secondary Area */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                              Role
                            </div>
                            <Select
                              value={selectedVolunteer?.assignedRole || ''}
                              onChange={(e, data) => {
                                if (selectedVolunteer) {
                                  updateVolunteer(selectedVolunteer.id, { assignedRole: data.value || '' });
                                  setSelectedVolunteer(prev => prev ? { ...prev, assignedRole: data.value || '' } : null);
                                }
                              }}
                              style={{ width: '100%' }}
                            >
                              <option value="">Select Role</option>
                              {getAvailableRoles(selectedVolunteer?.businessUnit).map(role => (
                                <option key={role} value={role}>{role}</option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        
                        {/* Areas of Interest */}
                        <div>
                          <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                            Areas of Interest
                          </div>
                          {selectedVolunteer?.volunteerRoleInterest ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {Array.isArray(selectedVolunteer?.volunteerRoleInterest) 
                                ? (selectedVolunteer?.volunteerRoleInterest as string[])?.map((interest: string, index: number) => (
                                    <div 
                                      key={index}
                                      className={styles.roleTag}
                                      style={{ 
                                        backgroundColor: tokens.colorPaletteTealBackground2,
                                        minWidth: '80px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        fontSize: '11px',
                                        padding: '4px 8px'
                                      }}
                                    >
                                      {interest.trim()}
                                    </div>
                                  ))
                                : (selectedVolunteer?.volunteerRoleInterest as string)?.split(',').map((interest: string, index: number) => (
                                    <div 
                                      key={index}
                                      className={styles.roleTag}
                                      style={{ 
                                        backgroundColor: tokens.colorPaletteTealBackground2,
                                        minWidth: '80px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        fontSize: '11px',
                                        padding: '4px 8px'
                                      }}
                                    >
                                      {interest.trim()}
                                    </div>
                                  ))
                              }
                            </div>
                          ) : (
                            <div style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic', fontSize: '12px' }}>
                              No areas of interest specified
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>



                  {/* Additional Information Section */}
                  {selectedVolunteer?.additionalInformation && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '16px',
                      backgroundColor: tokens.colorNeutralBackground2,
                      borderRadius: '6px',
                      border: `1px solid ${tokens.colorNeutralStroke1}`
                    }}>
                      <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '8px' }}>
                        Additional Information
                      </div>
                      <div style={{ fontSize: '14px', lineHeight: '1.5', color: tokens.colorNeutralForeground1 }}>
                        {selectedVolunteer?.additionalInformation}
                      </div>
                    </div>
                  )}

                  {/* Compliance Status Section */}
                  <div style={{ 
                    marginTop: '20px',
                    padding: '16px',
                    backgroundColor: tokens.colorNeutralBackground2,
                    borderRadius: '6px',
                    border: `1px solid ${tokens.colorNeutralStroke1}`
                  }}>
                    <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '16px' }}>
                      Compliance Status
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: selectedVolunteer?.addedToWhatsapp ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2
                        }} />
                        <span style={{ fontSize: '12px' }}>Added to WhatsApp</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: selectedVolunteer?.orientationCompleted ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2
                        }} />
                        <span style={{ fontSize: '12px' }}>Orientation Completed</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: selectedVolunteer?.backgroundCleared ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2
                        }} />
                        <span style={{ fontSize: '12px' }}>Background Check Cleared</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: selectedVolunteer?.confidentiality ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2
                        }} />
                        <span style={{ fontSize: '12px' }}>Credential Verification</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: selectedVolunteer?.hipaaPrivacyTrainingCompleted ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2
                        }} />
                        <span style={{ fontSize: '12px' }}>HIPAA Privacy Training</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: selectedVolunteer?.hipaaSecurityTrainingCompleted ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2
                        }} />
                        <span style={{ fontSize: '12px' }}>HIPAA Security Training</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: selectedVolunteer?.scheduledForUpcomingClinic ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2
                        }} />
                        <span style={{ fontSize: '12px' }}>Scheduled for Clinic</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: selectedVolunteer?.consentFormSent ? tokens.colorPaletteGreenBackground2 : tokens.colorPaletteRedBackground2
                        }} />
                        <span style={{ fontSize: '12px' }}>Consent Form Sent</span>
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  {selectedVolunteer?.notes && (
                    <div style={{ 
                      marginTop: '20px',
                      padding: '16px',
                      backgroundColor: tokens.colorNeutralBackground2,
                      borderRadius: '6px',
                      border: `1px solid ${tokens.colorNeutralStroke1}`
                    }}>
                      <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '8px' }}>
                        Notes
                      </div>
                      <div style={{ fontSize: '14px', lineHeight: '1.5', color: tokens.colorNeutralForeground1 }}>
                        {selectedVolunteer?.notes}
                      </div>
                    </div>
                  )}

                  {/* Monthly Availability Section */}
                  <div className={styles.monthlySection}>
                    <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '16px' }}>
                      Monthly Availability
                    </div>
                    <div className={styles.monthlyGrid}>
                      {['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'].map((month) => {
                        const monthKey = `${month.toLowerCase()}Availability` as keyof Volunteer;
                        const availability = selectedVolunteer?.[monthKey] as string;
                        
                        let cardClass = styles.noData;
                        let badgeClass = styles.noDataBadge;
                        let availabilityText = 'No Data';
                        
                        if (availability === 'Available') {
                          cardClass = styles.hasAvailability;
                          badgeClass = styles.availableBadge;
                          availabilityText = 'Available';
                        } else if (availability === 'Not Available') {
                          cardClass = styles.noAvailability;
                          badgeClass = styles.unavailableBadge;
                          availabilityText = 'Not Available';
                        }
                        
                        const handleAvailabilityToggle = async () => {
                          if (!selectedVolunteer || updatingMonth) return;
                          
                          setUpdatingMonth(month);
                          
                          let newStatus: string;
                          if (availability === 'Available') {
                            newStatus = 'Not Available';
                          } else if (availability === 'Not Available') {
                            newStatus = 'Available';
                          } else {
                            newStatus = 'Available'; // Default to Available if no data
                          }
                          
                          try {
                            await updateVolunteer(selectedVolunteer.id, { [monthKey]: newStatus });
                            // Update the selectedVolunteer state to reflect the change in the UI
                            setSelectedVolunteer(prev => prev ? { ...prev, [monthKey]: newStatus } : null);
                          } catch (error) {
                            console.error('Error updating availability:', error);
                          } finally {
                            setUpdatingMonth(null);
                          }
                        };
                        
                        return (
                          <div 
                            key={month} 
                            className={`${styles.monthCard} ${cardClass}`}
                            onClick={updatingMonth === month ? undefined : handleAvailabilityToggle}
                            style={{ 
                              cursor: updatingMonth === month ? 'not-allowed' : 'pointer',
                              opacity: updatingMonth === month ? 0.6 : 1
                            }}
                            title={updatingMonth === month ? 'Updating...' : `Click to toggle ${month} availability`}
                          >
                            <div className={styles.monthHeader}>
                              <div className={styles.monthTitle}>{month}</div>
                            </div>
                            <div className={styles.monthContent}>
                              <div className={styles.availabilityDetails}>
                                {updatingMonth === month ? (
                                  <Spinner size="tiny" label="Updating..." />
                                ) : (
                                  <Badge appearance="filled" className={badgeClass}>
                                    {availabilityText}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Monthly Summary */}
                    <div className={styles.monthlySummary}>
                      <div className={styles.summaryStats}>
                        <div className={styles.summaryStat}>
                          <span className={styles.statLabel}>Available Months:</span>
                          <span className={styles.statValue}>
                            {['January', 'February', 'March', 'April', 'May', 'June', 
                              'July', 'August', 'September', 'October', 'November', 'December'].filter(month => {
                              const monthKey = `${month.toLowerCase()}Availability` as keyof Volunteer;
                              return selectedVolunteer?.[monthKey] === 'Available';
                            }).length}
                          </span>
                        </div>
                        <div className={styles.summaryStat}>
                          <span className={styles.statLabel}>Unavailable Months:</span>
                          <span className={styles.statValue}>
                            {['January', 'February', 'March', 'April', 'May', 'June', 
                              'July', 'August', 'September', 'October', 'November', 'December'].filter(month => {
                              const monthKey = `${month.toLowerCase()}Availability` as keyof Volunteer;
                              return selectedVolunteer?.[monthKey] === 'Not Available';
                            }).length}
                          </span>
                        </div>
                        <div className={styles.summaryStat}>
                          <span className={styles.statLabel}>No Data:</span>
                          <span className={styles.statValue}>
                            {['January', 'February', 'March', 'April', 'May', 'June', 
                              'July', 'August', 'September', 'October', 'November', 'December'].filter(month => {
                              const monthKey = `${month.toLowerCase()}Availability` as keyof Volunteer;
                              return !selectedVolunteer?.[monthKey] || selectedVolunteer?.[monthKey] === '';
                            }).length}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Comments Section */}
                  <div className={styles.commentsSection}>
                    <div className={styles.commentsHeader}>
                      <h3>Team Comments ({comments.length})</h3>
                      <Button
                        appearance="subtle"
                        icon={commentsExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
                        onClick={() => setCommentsExpanded(!commentsExpanded)}
                        size="small"
                      >
                        {commentsExpanded ? 'Collapse' : 'Expand'}
                      </Button>
                    </div>
                    
                    {commentsExpanded && (
                      <>
                        {loadingComments ? (
                          <div className={styles.commentsLoading}>
                            <Spinner size="small" label="Loading comments..." />
                          </div>
                        ) : comments.length > 0 ? (
                          <div className={styles.commentsList}>
                            {comments.map((comment) => (
                              <div key={comment.id} className={styles.commentItem}>
                                <div className={styles.commentHeader}>
                                  <span className={styles.commentAuthor}>
                                    {comment.author}
                                  </span>
                                  <span className={styles.commentDate}>
                                    {new Date(comment.timestamp).toLocaleDateString()} at {new Date(comment.timestamp).toLocaleTimeString()}
                                  </span>
                                </div>
                                <div className={styles.commentContent}>
                                  {comment.content}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={styles.noComments}>
                            No comments yet. Add the first comment below.
                          </div>
                        )}
                        
                        {/* Add Comment Section */}
                        <div className={styles.addCommentSection}>
                          <Textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment about this volunteer..."
                            rows={4}
                            className={styles.commentInput}
                            disabled={submittingComment}
                          />
                          <div className={styles.commentActions}>
                            <Button
                              appearance="primary"
                              size="small"
                              onClick={handleAddComment}
                              disabled={!newComment.trim() || submittingComment}
                            >
                              {submittingComment ? (
                                <>
                                  <Spinner size="tiny" />
                                  Adding...
                                </>
                              ) : (
                                'Add Comment'
                              )}
                            </Button>
                            <Button
                              appearance="subtle"
                              size="small"
                              onClick={() => setNewComment('')}
                              disabled={submittingComment}
                            >
                              Clear
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setIsModalOpen(false)}>
                  Close
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {isPreviewOverlayOpen && (
        <PreviewOverlay
          assignments={assignments}
          currentMonthName={currentMonthName}
          onClose={() => setIsPreviewOverlayOpen(false)}
        />
      )}
    </div>
  );
};

export default VolunteerScheduler; 